import type { DevToolLink, AppToolLink } from '@bizfirst/common-auth-react';
import businessAppsData from '../../../../apps/global/public/data/getBusinessApps.json';
import developerToolsData from '../../../../apps/global/public/data/getDeveloperTools.json';

export interface GlobalAppData {
  /** "Developer Tools" gallery entries — AuthHeaderControls' `devTools` prop. Sourced
   *  from the global app's data/getBusinessApps.json (filenames are intentionally not
   *  1:1 with the props they feed — a repo-owner naming choice, don't "fix" it). */
  businessApps: DevToolLink[];
  /** "Apps Tools" row entries — AuthHeaderControls' `appTools` prop. Sourced from
   *  the global app's data/getDeveloperTools.json. */
  appTools: AppToolLink[];
}

/**
 * The shared, cross-app static data also hosted by the `@bizfirst/global` app
 * (`src/bizfirst-global/apps/global/public/data/`) — the Developer Tools gallery
 * and per-app tool shortcuts every BizFirst app can reuse. Lives in its own
 * package (not duplicated per consuming app) for the same reason
 * `@bizfirst/notifications-react` does — one client, reused everywhere, not
 * hand-rolled per app.
 *
 * Bundled at build time (a plain JSON import), not fetched over HTTP — the
 * repo owner decided a runtime dependency on the `global` app actually
 * running is too much friction for local dev ("hard dependency for
 * developers"). `global`'s `public/data/` stays the one canonical source
 * file — this package just consumes it a different way, so the data still
 * isn't duplicated/hand-copied anywhere. If `global` ever needs to serve
 * this over HTTP for some other consumer (a non-JS app, e.g.), that's still
 * possible independently of how this package gets it.
 */
export function useGlobalAppData(): GlobalAppData {
  return {
    businessApps: businessAppsData as DevToolLink[],
    appTools: developerToolsData as AppToolLink[],
  };
}
